import React, { useState } from 'react';
import styles from './FilterGroup.module.scss'; // Reuse FilterGroup styles

/**
 * Collapsible category group component
 * Shows a parent category with child categories as checkboxes (accordion style)
 *
 * @param {Object} props
 * @param {string} props.title - Parent category title (Level 1)
 * @param {Array} props.children - Array of child category objects {slug, title, count}
 * @param {Array} props.selectedCategories - Array of selected child category slugs
 * @param {Array} props.availableCategories - Array of category slugs that have matching products (optional)
 * @param {Function} props.onChange - Callback when selection changes (categorySlug, isSelected)
 */
export default function CategoryGroup({ title, children = [], selectedCategories = [], availableCategories = null, onChange }) {
    const [isOpen, setIsOpen] = useState(false);

    const handleCheckboxChange = (categorySlug) => {
        const isSelected = selectedCategories.includes(categorySlug);
        onChange(categorySlug, !isSelected);
    };

    // Check if any children are selected
    const hasSelected = children.some(child => selectedCategories.includes(child.slug));

    // Check if any children in this group are available
    // If availableCategories is null, all categories are enabled (default behavior)
    // If it's an array, only enable categories that are in the array OR already selected
    const hasAvailableChildren = availableCategories === null ||
        children.some(child =>
            availableCategories.includes(child.slug) || selectedCategories.includes(child.slug)
        );

    // Don't render if no children
    if (children.length === 0) {
        return null;
    }

    return (
        <li className={`${styles.mainCategoryItem} ${(isOpen || hasSelected) && hasAvailableChildren ? styles.active : ''} ${!hasAvailableChildren ? styles.noAvailableFilters : ''}`}>
            <div
                className={styles.mainCategory}
                onClick={() => setIsOpen(!isOpen)}
            >
                {title}
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    width="16"
                    height="16"
                >
                    <path fill="currentColor" d="M7 10l5 5 5-5z" />
                </svg>
            </div>
            {(isOpen || hasSelected) && (
                <ul className={styles.subCategoryItem}>
                    {children.map((child) => {
                        // Determine if category should be disabled
                        // If availableCategories is null, all categories are enabled (default behavior)
                        // If it's an array, only enable categories that are in the array OR already selected
                        const isAvailable = availableCategories === null ||
                                          availableCategories.includes(child.slug) ||
                                          selectedCategories.includes(child.slug);

                        return (
                            <li
                                key={child.slug}
                                className={!isAvailable ? styles.disabled : ''}
                            >
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={selectedCategories.includes(child.slug)}
                                        disabled={!isAvailable}
                                        onChange={() => handleCheckboxChange(child.slug)}
                                    />
                                    <span className={styles.categoryLabel}>
                                        {child.title}
                                        {child.count > 0 && (
                                            <span className={styles.categoryCount}>{child.count}</span>
                                        )}
                                    </span>
                                </label>
                            </li>
                        );
                    })}
                </ul>
            )}
        </li>
    );
}
